import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';

/**
 * ScriptUnlock i18n - Global Language System
 *
 * Supports 18 languages across 36 markets.
 * Only English is bundled eagerly - all others load on demand (code-split).
 * Language auto-detection order: localStorage → market default → browser → en
 */

export const languages = [
  { code: 'en',    name: 'English',    flag: '🇺🇸', nativeName: 'English' },
  { code: 'es',    name: 'Spanish',    flag: '🇪🇸', nativeName: 'Español' },
  { code: 'fr',    name: 'French',     flag: '🇫🇷', nativeName: 'Français' },
  { code: 'de',    name: 'German',     flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'pt',    name: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
  { code: 'it',    name: 'Italian',    flag: '🇮🇹', nativeName: 'Italiano' },
  { code: 'nl',    name: 'Dutch',      flag: '🇳🇱', nativeName: 'Nederlands' },
  { code: 'pl',    name: 'Polish',     flag: '🇵🇱', nativeName: 'Polski' },
  { code: 'sv',    name: 'Swedish',    flag: '🇸🇪', nativeName: 'Svenska' },
  { code: 'zh',    name: 'Chinese (Simplified)',  flag: '🇨🇳', nativeName: '中文(简体)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', flag: '🇭🇰', nativeName: '中文(繁體)' },
  { code: 'ja',    name: 'Japanese',   flag: '🇯🇵', nativeName: '日本語' },
  { code: 'ko',    name: 'Korean',     flag: '🇰🇷', nativeName: '한국어' },
  { code: 'ar',    name: 'Arabic',     flag: '🇸🇦', nativeName: 'العربية', rtl: true },
  { code: 'hi',    name: 'Hindi',      flag: '🇮🇳', nativeName: 'हिन्दी' },
  { code: 'th',    name: 'Thai',       flag: '🇹🇭', nativeName: 'ภาษาไทย' },
  { code: 'ms',    name: 'Malay',      flag: '🇲🇾', nativeName: 'Bahasa Melayu' },
] as const;

export type LanguageCode = typeof languages[number]['code'];

export const RTL_LANGUAGES = languages.filter((l) => (l as any).rtl).map(l => l.code);

export function isRTL(code: string): boolean {
  return RTL_LANGUAGES.includes(code as LanguageCode);
}

/** Get native name for a language code */
export function getLanguageName(code: string): string {
  return languages.find(l => l.code === code)?.nativeName || code;
}

// Dynamic locale loaders - new locales auto-load when files are present.
// Locales not yet translated gracefully fall back to English.
const localeLoaders: Record<string, () => Promise<any>> = {
  es:      () => import('./locales/es.json'),
  fr:      () => import('./locales/fr.json'),
  de:      () => import('./locales/de.json'),
  pt:      () => import('./locales/pt.json'),
  it:      () => import('./locales/it.json'),
  zh:      () => import('./locales/zh.json'),
  ja:      () => import('./locales/ja.json'),
  // Phase 2 locales - loaded when files exist (added by subagent)
  nl:      () => import('./locales/nl.json').catch(() => import('./locales/en.json')),
  pl:      () => import('./locales/pl.json').catch(() => import('./locales/en.json')),
  sv:      () => import('./locales/sv.json').catch(() => import('./locales/en.json')),
  ko:      () => import('./locales/ko.json').catch(() => import('./locales/en.json')),
  ar:      () => import('./locales/ar.json').catch(() => import('./locales/en.json')),
  hi:      () => import('./locales/hi.json').catch(() => import('./locales/en.json')),
  th:      () => import('./locales/th.json').catch(() => import('./locales/en.json')),
  ms:      () => import('./locales/ms.json').catch(() => import('./locales/en.json')),
  'zh-TW': () => import('./locales/zh-TW.json').catch(() => import('./locales/zh.json')),
};

async function loadLocale(lng: string): Promise<void> {
  if (lng === 'en' || i18n.hasResourceBundle(lng, 'translation')) return;
  const loader = localeLoaders[lng];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default ?? mod, true, true);
    // Apply RTL direction to document if needed
    if (typeof document !== 'undefined') {
      document.documentElement.dir = isRTL(lng) ? 'rtl' : 'ltr';
    }
  } catch {
    // Fallback to English on load failure
  }
}

/**
 * Set language for a market - loads locale on demand and applies RTL if needed.
 * Call this when user selects a country or changes language.
 */
export async function setMarketLanguage(languageCode: string): Promise<void> {
  const base = languageCode.split('-')[0];
  await loadLocale(languageCode);
  if (!i18n.hasResourceBundle(languageCode, 'translation')) {
    await loadLocale(base);
  }
  const target = i18n.hasResourceBundle(languageCode, 'translation') ? languageCode : base;
  await i18n.changeLanguage(target);
  if (typeof window !== 'undefined') {
    localStorage.setItem('i18nextLng', target);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    partialBundledLanguages: true,
  });

// Load detected language immediately if not English
const detected = i18n.language?.split('-')[0];
if (detected && detected !== 'en') {
  loadLocale(i18n.language);
  if (detected !== i18n.language) loadLocale(detected);
}

i18n.on('languageChanged', (lng) => {
  loadLocale(lng);
  if (typeof document !== 'undefined') {
    document.documentElement.dir = isRTL(lng.split('-')[0]) ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  }
});

export { loadLocale };
export default i18n;
